import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot 
} from 'firebase/firestore';

const DatabaseContext = createContext();

export const useDatabase = () => {
  const context = useContext(DatabaseContext);
  if (!context) {
    throw new Error('useDatabase must be used within a DatabaseProvider');
  }
  return context;
};

// Firestore operations
export const loadAllModuleData = async () => {
  try {
    const chartsSnapshot = await getDocs(collection(db, 'charts'));
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const analyticsDoc = await getDoc(doc(db, 'analytics', 'metrics'));

    return {
      charts: chartsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      users: usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      analytics: analyticsDoc.exists() ? analyticsDoc.data() : {}
    };
  } catch (error) {
    console.error('Error loading module data:', error);
    throw error;
  }
};

export const DatabaseProvider = ({ children }) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});

  // Firestore collection names
  const collections = {
    charts: 'charts',
    analytics: 'analytics',
    users: 'users',
    metrics: 'metrics',
    feed: 'feed',
    tables: 'tables'
  };

  // Generic Firestore fetch function
  const fetchData = async (collectionName, params = {}) => {
    const cacheKey = `${collectionName}_${JSON.stringify(params)}`;
    
    if (data[cacheKey]) {
      return data[cacheKey];
    }

    if (!db) {
      setErrors(prev => ({ ...prev, [cacheKey]: 'Firestore not available' }));
      throw new Error('Firestore not available');
    }

    setLoading(prev => ({ ...prev, [cacheKey]: true }));
    setErrors(prev => ({ ...prev, [cacheKey]: null }));

    try {
      let queryRef = collection(db, collectionName);
      
      // Apply query constraints if provided
      if (params.where) {
        console.log('🔍 Firestore GET request:', {
          collection: collectionName,
          where: params.where,
          query: `${params.where.field} ${params.where.operator} "${params.where.value}"`
        });
        queryRef = query(queryRef, where(params.where.field, params.where.operator, params.where.value));
      }
      if (params.orderBy) {
        queryRef = query(queryRef, orderBy(params.orderBy.field, params.orderBy.direction || 'asc'));
      }
      if (params.limit) {
        queryRef = query(queryRef, limit(params.limit));
      }

      const snapshot = await getDocs(queryRef);
      const result = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      setData(prev => ({ ...prev, [cacheKey]: result }));
      return result;
    } catch (error) {
      setErrors(prev => ({ ...prev, [cacheKey]: error.message }));
      throw error;
    } finally {
      setLoading(prev => ({ ...prev, [cacheKey]: false }));
    }
  };

  // Get a single document by ID
  const getDocument = async (collectionName, docId) => {
    const cacheKey = `${collectionName}_${docId}`;
    
    setLoading(prev => ({ ...prev, [cacheKey]: true }));
    setErrors(prev => ({ ...prev, [cacheKey]: null }));

    try {
      const docRef = doc(db, collectionName, docId);
      const snapshot = await getDoc(docRef);
      
      if (!snapshot.exists()) {
        throw new Error('Document does not exist');
      }

      const result = { id: snapshot.id, ...snapshot.data() };
      setData(prev => ({ ...prev, [cacheKey]: result }));
      return result;
    } catch (error) {
      setErrors(prev => ({ ...prev, [cacheKey]: error.message }));
      throw error;
    } finally {
      setLoading(prev => ({ ...prev, [cacheKey]: false }));
    }
  };

  // Add a new document
  const addDocument = async (collectionName, documentData) => {
    console.log('🔍 Adding document to collection:', collectionName);
    console.log('🔍 Document data:', JSON.stringify(documentData, null, 2));
    
    // Check auth state
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    console.log('🔍 Auth state:', {
      currentUser: auth.currentUser ? {
        uid: auth.currentUser.uid,
        email: auth.currentUser.email,
        displayName: auth.currentUser.displayName
      } : null,
      isSignedIn: !!auth.currentUser
    });
    
    setLoading(prev => ({ ...prev, [collectionName]: true }));
    setErrors(prev => ({ ...prev, [collectionName]: null }));

    try {
      console.log('🔍 Final data being sent to Firestore:', JSON.stringify({
        ...documentData,
        createdAt: new Date().toISOString()
      }, null, 2));
      
      const docRef = await addDoc(collection(db, collectionName), {
        ...documentData,
        createdAt: new Date().toISOString()
      });
      
      // Clear cache for this collection
      setData(prev => {
        const newData = { ...prev };
        Object.keys(newData).forEach(key => {
          if (key.startsWith(collectionName)) {
            delete newData[key];
          }
        });
        return newData;
      });

      return { id: docRef.id, ...documentData };
    } catch (error) {
      setErrors(prev => ({ ...prev, [collectionName]: error.message }));
      throw error;
    } finally {
      setLoading(prev => ({ ...prev, [collectionName]: false }));
    }
  };

  // Update a document
  const updateDocument = async (collectionName, docId, updateData) => {
    const cacheKey = `${collectionName}_${docId}`;
    setLoading(prev => ({ ...prev, [cacheKey]: true }));
    setErrors(prev => ({ ...prev, [cacheKey]: null }));

    try {
      console.log('🔍 Updating document:', { collectionName, docId, updateData });
      const docRef = doc(db, collectionName, docId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: new Date().toISOString()
      });
      
      // Clear cache
      setData(prev => {
        const newData = { ...prev };
        Object.keys(newData).forEach(key => {
          if (key.startsWith(collectionName)) {
            delete newData[key];
          }
        });
        return newData;
      });

      return { id: docId, ...updateData };
    } catch (error) {
      setErrors(prev => ({ ...prev, [cacheKey]: error.message }));
      throw error;
    } finally {
      setLoading(prev => ({ ...prev, [cacheKey]: false }));
    }
  };

  // Delete a document
  const deleteDocument = async (collectionName, docId) => {
    const cacheKey = `${collectionName}_${docId}`;
    setLoading(prev => ({ ...prev, [cacheKey]: true }));
    setErrors(prev => ({ ...prev, [cacheKey]: null }));

    try {
      const docRef = doc(db, collectionName, docId);
      await deleteDoc(docRef);
      
      // Clear cache
      setData(prev => {
        const newData = { ...prev };
        Object.keys(newData).forEach(key => {
          if (key.startsWith(collectionName)) {
            delete newData[key];
          }
        });
        return newData;
      });

      return { id: docId };
    } catch (error) {
      setErrors(prev => ({ ...prev, [cacheKey]: error.message }));
      throw error;
    } finally {
      setLoading(prev => ({ ...prev, [cacheKey]: false }));
    }
  };

  // Real-time listener for a collection
  const subscribeToCollection = (collectionName, params = {}, callback) => {
    let queryRef = collection(db, collectionName);
    
    if (params.where) {
      queryRef = query(queryRef, where(params.where.field, params.where.operator, params.where.value));
    }
    if (params.orderBy) {
      queryRef = query(queryRef, orderBy(params.orderBy.field, params.orderBy.direction || 'asc'));
    }
    if (params.limit) {
      queryRef = query(queryRef, limit(params.limit));
    }

    const unsubscribe = onSnapshot(queryRef, (snapshot) => {
      const result = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(result);
    }, (error) => {
      console.error('Subscription error:', error);
    });

    return unsubscribe;
  };

  // Real-time listener for a single document
  const subscribeToDocument = (collectionName, docId, callback) => {
    const docRef = doc(db, collectionName, docId);
    
    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        callback({ id: snapshot.id, ...snapshot.data() });
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('Subscription error:', error);
    });

    return unsubscribe;
  };

  // Refresh data (clear cache and refetch)
  const refreshData = async (collectionName, params = {}) => {
    const cacheKey = `${collectionName}_${JSON.stringify(params)}`;
    
    setData(prev => {
      const newData = { ...prev };
      delete newData[cacheKey];
      return newData;
    });
    
    return fetchData(collectionName, params);
  };

  // Export data
  const exportData = async (collectionName, params = {}, format = 'json') => {
    try {
      const dataToExport = await fetchData(collectionName, params);
      
      if (format === 'json') {
        const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${collectionName}_${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (format === 'csv') {
        const csv = convertToCSV(dataToExport);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${collectionName}_${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  };

  // Convert data to CSV
  const convertToCSV = (data) => {
    if (!Array.isArray(data) || data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(',')];
    
    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header];
        return typeof value === 'string' && value.includes(',') 
          ? `"${value}"` 
          : value;
      });
      csvRows.push(values.join(','));
    }
    
    return csvRows.join('\n');
  };

  // Check if data is loading
  const isLoading = (collectionName, params = {}) => {
    const cacheKey = `${collectionName}_${JSON.stringify(params)}`;
    return loading[cacheKey] || false;
  };

  // Get error for collection
  const getError = (collectionName, params = {}) => {
    const cacheKey = `${collectionName}_${JSON.stringify(params)}`;
    return errors[cacheKey] || null;
  };

  const value = {
    data,
    loading,
    errors,
    fetchData,
    getDocument,
    addDocument,
    updateDocument,
    deleteDocument,
    subscribeToCollection,
    subscribeToDocument,
    refreshData,
    exportData,
    isLoading,
    getError,
    collections
  };

  return (
    <DatabaseContext.Provider value={value}>
      {children}
    </DatabaseContext.Provider>
  );
};

export default DatabaseContext;
